<template>
  <b-card>
    <b-row
      v-if="totalItems > visibleParcelItems.length || totalPages > 1"
      class="mb-1 align-items-center"
    >
      <b-col>
        <span class="text-muted small">
          Showing page {{ currentPage }} of {{ totalPages }} ({{ totalItems }} items total)
          <b-spinner
            v-if="isLoadingItems"
            small
            class="ml-1"
          />
          <span
            v-if="itemsWithIssuesCount > 0"
            class="ml-1 text-danger font-weight-bold"
          >
            ({{ itemsWithIssuesCount }} with errors — shown first)
          </span>
        </span>
      </b-col>
      <b-col class="text-right">
        <b-button
          :disabled="currentPage <= 1 || isLoadingItems"
          size="sm"
          variant="outline-secondary"
          class="mr-1"
          @click="$emit('change-page', currentPage - 1)"
        >
          &laquo; Prev
        </b-button>
        <b-button
          :disabled="currentPage >= totalPages || isLoadingItems"
          size="sm"
          variant="outline-secondary"
          @click="$emit('change-page', currentPage + 1)"
        >
          Next &raquo;
        </b-button>
      </b-col>
    </b-row>
    <b-overlay
      :show="isLoadingItems"
      opacity="0.6"
      spinner-variant="primary"
    >
      <b-table
        ref="parcelItemsTable"
        :items="visibleParcelItems"
        :fields="itemFields"
        show-empty
        empty-text="No goods items found"
        @row-clicked="handleRowClicked"
      >
        <template #head(show_details)>
          <b-button
            v-if="!disableChanges"
            id="add-parcel-item-h1"
            variant="info"
            size="sm"
            @click="$emit('add-item')"
          >
            <feather-icon icon="PlusIcon" />
            Add row
          </b-button>
        </template>
        <template #cell(show_details)="row">
          <b-button
            size="sm"
            variant="link"
            @click="handleRowClicked(row.item, row.index)"
          >
            <feather-icon
              :icon="row.detailsShowing ? 'ChevronDownIcon' : 'ChevronRightIcon'"
              size="18"
            />
          </b-button>
        </template>

        <template #cell(item_number)="row">
          {{ parcelItems.indexOf(row.item) + 1 }}
        </template>

        <template #cell(actions)="row">
          <b-button
            v-if="!disableChanges"
            size="sm"
            variant="danger"
            @click="$emit('remove-item', row.item)"
          >
            <feather-icon icon="TrashIcon" />
          </b-button>
        </template>

        <template #row-details="row">
          <b-card>
            <goods-section
              ref="goodsSections"
              :item="row.item"
              :disabled="disableChanges"
              :countries-options="countriesOptions"
            />

            <supplementary-units-section
              :item="row.item"
              :disabled="disableChanges"
            />

            <transactions-section
              ref="transactionsSections"
              :item="row.item"
              :disabled="disableChanges"
            />

            <additions-deductions-section
              :item="row.item"
              :disabled="disableChanges"
            />

            <packaging-section
              :item="row.item"
              :disabled="disableChanges"
            />

            <supporting-documents-section
              :item="row.item"
              :disabled="disableChanges"
              :shipment-id="shipmentId"
              :parcel-id="parcelId"
            />
          </b-card>
        </template>
      </b-table>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BButton,
  BCard,
  BOverlay,
  BTable,
  BRow,
  BCol,
  BSpinner,
} from 'bootstrap-vue'
import GoodsSection from '@/views/shipments/declarations/modals/components/h1/GoodsSection.vue'
import SupplementaryUnitsSection from '@/views/shipments/declarations/modals/components/h1/SupplementaryUnitsSection.vue'
import TransactionsSection from '@/views/shipments/declarations/modals/components/h1/TransactionsSection.vue'
import AdditionsDeductionsSection from '@/views/shipments/declarations/modals/components/h1/AdditionsDeductionsSection.vue'
import PackagingSection from '@/views/shipments/declarations/modals/components/h1/PackagingSection.vue'
import SupportingDocumentsSection from '@/views/shipments/declarations/modals/components/h1/SupportingDocumentsSection.vue'

export default {
  name: 'H1ParcelItemsTab',
  components: {
    BButton,
    BCard,
    BOverlay,
    BTable,
    BRow,
    BCol,
    BSpinner,
    GoodsSection,
    SupplementaryUnitsSection,
    TransactionsSection,
    AdditionsDeductionsSection,
    PackagingSection,
    SupportingDocumentsSection,
  },
  props: {
    parcelItems: {
      type: Array,
      required: true,
    },
    visibleParcelItems: {
      type: Array,
      required: true,
    },
    itemFields: {
      type: Array,
      required: true,
    },
    disableChanges: {
      type: Boolean,
      default: false,
    },
    countriesOptions: {
      type: Array,
      default: () => [],
    },
    shipmentId: {
      type: Number,
      default: null,
    },
    parcelId: {
      type: Number,
      default: null,
    },
    currentItemIndex: {
      type: Number,
      default: 0,
    },
    totalItems: {
      type: Number,
      default: 0,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    totalPages: {
      type: Number,
      default: 1,
    },
    isLoadingItems: {
      type: Boolean,
      default: false,
    },
    itemsWithIssuesCount: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    handleRowClicked(item, index) {
      const table = this.$refs.parcelItemsTable
      if (!table || !table.computedItems[index]) return

      const row = table.computedItems[index]
      // eslint-disable-next-line no-underscore-dangle
      const isCurrentlyShowing = row._showDetails

      if (isCurrentlyShowing) {
        table.$set(row, '_showDetails', false)
        this.parcelItems.forEach(parcelItem => {
          parcelItem.isHidden = false
        })
      } else {
        this.expandItem(this.parcelItems.indexOf(item))
      }
    },
    /**
     * Expand a specific goods item by its index in the full parcelItems array.
     * Hides all other items, collapses their details, and scrolls to the target.
     */
    expandItem(index) {
      if (index < 0 || index >= this.parcelItems.length) return

      const table = this.$refs.parcelItemsTable
      const targetItem = this.parcelItems[index]

      // Hide all items except the target
      this.parcelItems.forEach((item, i) => {
        item.isHidden = i !== index
      })

      this.$emit('update:currentItemIndex', index)

      this.$nextTick(() => {
        if (table) {
          // Collapse all rows first
          table.computedItems.forEach(r => {
            table.$set(r, '_showDetails', false)
          })

          this.$nextTick(() => {
            // Expand the target row (it should be the only visible one after filtering)
            if (table.computedItems.length > 0) {
              table.$set(targetItem, '_showDetails', true)
            }

            // Scroll to the expanded row
            this.$nextTick(() => {
              const tableEl = table.$el
              if (!tableEl) return
              const mainRows = tableEl.querySelectorAll('tbody tr:not(.b-table-details)')
              const rowElement = mainRows[0]
              if (!rowElement) return
              const modalBody = document.querySelector('.h1-parcel-modal-body')
              if (modalBody) {
                const rowRect = rowElement.getBoundingClientRect()
                const modalBodyRect = modalBody.getBoundingClientRect()
                modalBody.scrollTop = rowRect.top - modalBodyRect.top + modalBody.scrollTop - 115
              }
            })
          })
        }
      })
    },
    /**
     * Collapse all expanded goods items and unhide all rows.
     */
    collapseAll() {
      const table = this.$refs.parcelItemsTable
      if (table) {
        table.computedItems.forEach(item => {
          table.$set(item, '_showDetails', false)
        })
      }
      this.parcelItems.forEach(item => {
        item.isHidden = false
      })
    },
    async validateAll() {
      const table = this.$refs.parcelItemsTable
      if (!table) return true

      // Expand all rows so their sections get mounted
      table.computedItems.forEach(row => {
        table.$set(row, '_showDetails', true)
      })
      await this.$nextTick()

      const goodsSections = [].concat(this.$refs.goodsSections || [])
      const transactionsSections = [].concat(this.$refs.transactionsSections || [])
      const all = [...goodsSections, ...transactionsSections]
      const results = await Promise.all(all.map(s => s.validate()))
      return results.every(Boolean)
    },
  },
}
</script>
